import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../services/api/apiService";

const PLATFORMS = [
  { key: "meta", label: "Meta", color: "bg-blue-600" },
  { key: "tiktok", label: "TikTok", color: "bg-black" },
  { key: "snapchat", label: "Snapchat", color: "bg-yellow-400 text-black" },
];

const PixelsManager = () => {
  const [platform, setPlatform] = useState("meta");
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  // Dataset Quality (Meta uniquement) — score EMQ chargé à la demande par pixel.
  const [quality, setQuality] = useState({}); // { [pixelId]: { loading, events } }

  const load = async (p) => {
    setLoading(true);
    try {
      const res = await getRequest(`/api/ads/pixels/${p}`);
      setPixels(res?.pixels || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || `Impossible de charger les pixels ${p}.`);
      setPixels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(platform); }, [platform]);

  const createPixel = async () => {
    if (!name.trim()) { toast.warn("Donnez un nom au pixel."); return; }
    setCreating(true);
    try {
      await postRequest(`/api/ads/pixels/${platform}`, { name: name.trim() });
      toast.success("Pixel créé.");
      setName("");
      load(platform);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Création du pixel impossible.");
    } finally {
      setCreating(false);
    }
  };

  const toggleQuality = async (pixelId) => {
    setQuality((prev) => {
      if (prev[pixelId]) { const n = { ...prev }; delete n[pixelId]; return n; }
      return { ...prev, [pixelId]: { loading: true, events: [] } };
    });
    if (quality[pixelId]) return; // déjà ouvert → on vient de le fermer, rien à charger
    try {
      const res = await getRequest(`/api/ads/pixels/meta/${pixelId}/quality`);
      setQuality((prev) => ({ ...prev, [pixelId]: { loading: false, events: res?.events || [] } }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Qualité des données indisponible.");
      setQuality((prev) => { const n = { ...prev }; delete n[pixelId]; return n; });
    }
  };

  const scoreColor = (score) => (score >= 7 ? "text-green-600" : score >= 4 ? "text-amber-600" : "text-red-600");

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-[#221C51] mb-1">Pixels</h1>
      <p className="text-sm text-gray-500 mb-6">
        Créez et gérez les pixels de conversion par plateforme — nécessaires pour les objectifs Ventes/Conversions
        lors de la création d'une campagne, et pour le suivi CAPI côté Tracking.
      </p>

      <div className="flex gap-2 mb-6">
        {PLATFORMS.map((p) => (
          <button key={p.key} onClick={() => setPlatform(p.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${platform === p.key ? `${p.color} text-white` : "bg-gray-100 text-gray-600"}`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <p className="text-xs font-semibold text-gray-500 mb-2">Nouveau pixel {PLATFORMS.find((p) => p.key === platform)?.label}</p>
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du pixel (ex: Site principal)"
            className="flex-1 px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]" />
          <button onClick={createPixel} disabled={creating}
            className="px-5 py-2 text-sm text-white bg-[#221C51] rounded-lg font-semibold disabled:opacity-50">
            {creating ? "Création…" : "Créer"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500">Pixels {PLATFORMS.find((p) => p.key === platform)?.label} existants</p>
          <button onClick={() => load(platform)} className="text-xs text-[#221C51] font-semibold">Actualiser</button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : pixels.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun pixel trouvé. Créez-en un ci-dessus, ou connectez un compte {PLATFORMS.find((p) => p.key === platform)?.label} qui en a déjà un.</p>
        ) : (
          <div className="space-y-2">
            {pixels.map((px) => (
              <div key={px.pixelId} className="rounded-lg border border-gray-100 bg-gray-50 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-sm font-semibold text-[#221C51]">{px.name || "(sans nom)"}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 font-mono">{px.pixelId}</span>
                    {platform === "meta" && (
                      <button onClick={() => toggleQuality(px.pixelId)} className="text-xs font-semibold text-[#221C51] hover:underline">
                        {quality[px.pixelId] ? "Masquer qualité" : "Qualité des données"}
                      </button>
                    )}
                  </div>
                </div>
                {quality[px.pixelId] && (
                  <div className="px-3 pb-3 border-t border-gray-200 bg-white">
                    {quality[px.pixelId].loading ? (
                      <p className="text-xs text-gray-400 pt-2">Chargement…</p>
                    ) : quality[px.pixelId].events.length === 0 ? (
                      <p className="text-xs text-gray-400 pt-2">Aucune donnée de qualité disponible pour ce pixel (pas encore d'événements, ou Conversions API non configurée).</p>
                    ) : (
                      <div className="pt-2 space-y-1.5">
                        {quality[px.pixelId].events.map((ev, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">{ev.event_name}</span>
                            <span className={`font-bold ${scoreColor(ev.event_match_quality?.composite_score ?? 0)}`}>
                              EMQ {ev.event_match_quality?.composite_score ?? "—"}/10
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PixelsManager;
